import type { LevelSet_QuestionType as LevelSetQuestionType } from '__generated__/graphql-types';
import type { LevelSet_Question as LevelSetQuestion } from '__generated__/graphql-types-do-not-use';

import type { QuizQuestionType } from 'bundles/compound-assessments/types/FormParts';
import type { LevelSet_MasteryResultsFragment as MasteryResultsFragment } from 'bundles/levelsets/levelsets-v2/api/__generated__/LevelSet_SubmitAttempt';
import type { LevelSetScore, MasteryLevel } from 'bundles/levelsets/levelsets-v2/constants';
import type { QuestionWithMetadata } from 'bundles/levelsets/levelsets-v2/types';

const convertToQuizQuestionType = (questionType: LevelSetQuestionType): QuizQuestionType => {
  switch (questionType) {
    case 'CHECKBOX':
      return 'checkbox';
    default:
      return 'mcq';
  }
};

export const convertToLevelSetQuestionType = (questionType: QuizQuestionType): LevelSetQuestionType => {
  switch (questionType) {
    case 'checkbox':
      return 'CHECKBOX';
    default:
      return 'MULTIPLE_CHOICE';
  }
};

export const convertToQuestionWithMetadata = (question: LevelSetQuestion): QuestionWithMetadata => ({
  questionVariant: {
    questionId: question.questionId + '@' + question.questionVersion,
    questionType: convertToQuizQuestionType(question.questionType),
    variant: {
      detailLevel: 'Full',
      definition: JSON.parse(question.question),
    },
  },
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  metadata: question.metadata[0],
});

export const convertToScores = (masteryLevelResults: MasteryResultsFragment | undefined): LevelSetScore[] => {
  switch (masteryLevelResults?.masteryLevel) {
    case undefined:
      return [
        { masteryLevel: 'MASTERY_LEVEL_1', masteryLevelScore: 0 },
        { masteryLevel: 'MASTERY_LEVEL_2', masteryLevelScore: 0 },
        { masteryLevel: 'MASTERY_LEVEL_3', masteryLevelScore: 0 },
      ];
    case 'MASTERY_LEVEL_1':
      return [
        { masteryLevel: 'MASTERY_LEVEL_1', masteryLevelScore: masteryLevelResults.masteryPercent },
        { masteryLevel: 'MASTERY_LEVEL_2', masteryLevelScore: 0 },
        { masteryLevel: 'MASTERY_LEVEL_3', masteryLevelScore: 0 },
      ];
    case 'MASTERY_LEVEL_2':
      return [
        { masteryLevel: 'MASTERY_LEVEL_1', masteryLevelScore: 1 },
        { masteryLevel: 'MASTERY_LEVEL_2', masteryLevelScore: masteryLevelResults.masteryPercent },
        { masteryLevel: 'MASTERY_LEVEL_3', masteryLevelScore: 0 },
      ];
    case 'MASTERY_LEVEL_3':
      return [
        { masteryLevel: 'MASTERY_LEVEL_1', masteryLevelScore: 1 },
        { masteryLevel: 'MASTERY_LEVEL_2', masteryLevelScore: 1 },
        { masteryLevel: 'MASTERY_LEVEL_3', masteryLevelScore: masteryLevelResults.masteryPercent },
      ];
    default:
      // If for some reason the scoring model gives a higher mastery level, we'll treat it as 100% at Level 3:
      return [
        { masteryLevel: 'MASTERY_LEVEL_1', masteryLevelScore: 1 },
        { masteryLevel: 'MASTERY_LEVEL_2', masteryLevelScore: 1 },
        { masteryLevel: 'MASTERY_LEVEL_3', masteryLevelScore: 1 },
      ];
  }
};

export const convertToLatestMasteryProgress = (scores: LevelSetScore[]) => {
  const attainedMasteryLevel: MasteryLevel | null =
    scores.filter((score) => score.masteryLevelScore === 1).at(-1)?.masteryLevel || null;

  const isFirstLevelInProgress = !attainedMasteryLevel && scores.some((score) => score.masteryLevelScore > 0);

  return {
    attainedMasteryLevel,
    isFirstLevelInProgress,
  };
};

export const getLatestMasteryProgress = (masteryLevel?: MasteryLevel, masteryPercent?: number) => {
  // We could check if the given level is at 100% and if so return it as attained, and if not return 1 level lower. But
  // if we use the above functions then we don't need to maintain separate logic for this function.
  const masteryLevelResults: MasteryResultsFragment | undefined =
    masteryLevel && typeof masteryPercent !== 'undefined' ? { masteryLevel, masteryPercent } : undefined;
  const scores = convertToScores(masteryLevelResults);

  const progress = convertToLatestMasteryProgress(scores);
  return progress;
};

export const getMasteryLevelNumber = (masteryLevel?: MasteryLevel | null): number | undefined => {
  switch (masteryLevel) {
    case 'MASTERY_LEVEL_1':
      return 1;
    case 'MASTERY_LEVEL_2':
      return 2;
    case 'MASTERY_LEVEL_3':
      return 3;
    case 'MASTERY_LEVEL_4':
      return 4;
    default:
      return undefined;
  }
};
